import { create } from 'zustand';
import type { SimulationTest } from '@k-ucap/shared';
import type { SchedulerRunStatus } from '@/domain/simulation/SimulationScheduler.js';

export interface RuleExecutionState {
  ruleId: string;
  messageType: number;
  generated: number;
  sent: number;
  dropped: number;
  pass: number;
  warn: number;
  fail: number;
  status: 'idle' | 'running' | 'paused' | 'done' | 'stopped';
  error?: string;
}

export interface SimulationExecutionState {
  runStatus: SchedulerRunStatus;
  isRunning: boolean;
  isPaused: boolean;
  elapsedMs: number;
  selectedTest: SimulationTest | null;
  ruleStates: Record<string, RuleExecutionState>;
  runId: string | null;
  startedAt: string | null;
  recordMode: 'NONE' | 'SUMMARY' | 'SAMPLE' | 'ALL' | null;
  runError: string | null;

  startExecution: (test: SimulationTest) => void;
  pauseExecution: () => void;
  resumeExecution: () => void;
  stopExecution: () => void;
  setRunStatus: (status: SchedulerRunStatus) => void;
  setRunError: (error: string | null) => void;
  setElapsedMs: (elapsedMs: number) => void;
  incrementRuleGenerated: (ruleId: string, severity: 'PASS' | 'WARN' | 'FAIL') => void;
  incrementRuleDropped: (ruleId: string) => void;
  setRuleStatus: (ruleId: string, status: RuleExecutionState['status'], error?: string) => void;
  tick: (deltaMs: number) => void;
  reset: () => void;
}

const initialState: Omit<
  SimulationExecutionState,
  | 'startExecution'
  | 'pauseExecution'
  | 'resumeExecution'
  | 'stopExecution'
  | 'setRunStatus'
  | 'setRunError'
  | 'setElapsedMs'
  | 'incrementRuleGenerated'
  | 'incrementRuleDropped'
  | 'setRuleStatus'
  | 'tick'
  | 'reset'
> = {
  runStatus: 'IDLE',
  isRunning: false,
  isPaused: false,
  elapsedMs: 0,
  selectedTest: null,
  ruleStates: {},
  runId: null,
  startedAt: null,
  recordMode: null,
  runError: null,
};

export const useSimulationExecutionStore = create<SimulationExecutionState>((set, get) => ({
  ...initialState,

  startExecution: (test) => {
    const ruleStates: Record<string, RuleExecutionState> = {};
    test.messageRules.forEach((rule) => {
      if (!rule.enabled) return;
      ruleStates[rule.id] = {
        ruleId: rule.id,
        messageType: rule.messageType,
        generated: 0,
        sent: 0,
        dropped: 0,
        pass: 0,
        warn: 0,
        fail: 0,
        status: 'idle',
      };
    });
    set({
      runStatus: 'RUNNING',
      isRunning: true,
      isPaused: false,
      elapsedMs: 0,
      selectedTest: test,
      ruleStates,
      runId: `run-${Date.now()}`,
      startedAt: new Date().toISOString(),
      recordMode: test.executionConfig.recordMode ?? 'SUMMARY',
      runError: null,
    });
  },

  pauseExecution: () => set({ isPaused: true, runStatus: 'PAUSED' }),
  resumeExecution: () => set({ isPaused: false, runStatus: 'RUNNING' }),

  stopExecution: () =>
    set({
      runStatus: 'STOPPED',
      isRunning: false,
      isPaused: false,
      elapsedMs: 0,
      selectedTest: null,
      ruleStates: {},
      runId: null,
      startedAt: null,
      recordMode: null,
      runError: null,
    }),

  setRunStatus: (status) => set({ runStatus: status }),
  setRunError: (error) => set({ runError: error }),
  setElapsedMs: (elapsedMs) => set({ elapsedMs }),

  incrementRuleGenerated: (ruleId, severity) => {
    const state = get();
    const ruleState = state.ruleStates[ruleId];
    if (!ruleState) return;
    set({
      ruleStates: {
        ...state.ruleStates,
        [ruleId]: {
          ...ruleState,
          generated: ruleState.generated + 1,
          sent: ruleState.sent + 1,
          pass: severity === 'PASS' ? ruleState.pass + 1 : ruleState.pass,
          warn: severity === 'WARN' ? ruleState.warn + 1 : ruleState.warn,
          fail: severity === 'FAIL' ? ruleState.fail + 1 : ruleState.fail,
        },
      },
    });
  },

  incrementRuleDropped: (ruleId) => {
    const state = get();
    const ruleState = state.ruleStates[ruleId];
    if (!ruleState) return;
    set({
      ruleStates: {
        ...state.ruleStates,
        [ruleId]: {
          ...ruleState,
          dropped: ruleState.dropped + 1,
        },
      },
    });
  },

  setRuleStatus: (ruleId, status, error) => {
    const state = get();
    const ruleState = state.ruleStates[ruleId];
    if (!ruleState) return;
    set({
      ruleStates: {
        ...state.ruleStates,
        [ruleId]: { ...ruleState, status, error },
      },
    });
  },

  tick: (deltaMs) => {
    const state = get();
    if (!state.isRunning || state.isPaused) return;
    set({ elapsedMs: state.elapsedMs + deltaMs });
  },

  reset: () => set(initialState),
}));

export default useSimulationExecutionStore;
